import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://phulhar-dham.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (!navigator.onLine) {
      return Promise.reject(
        Object.assign(new Error('No internet connection'), { isOffline: true })
      );
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network error (no response received) or explicit offline flag
    if (error.isOffline || !error.response) {
      const event = new CustomEvent('app:offline-error');
      window.dispatchEvent(event);
      return Promise.reject(
        Object.assign(error, { isOffline: true, message: 'No internet connection' })
      );
    }

    const isLoginRequest = error.config?.url?.includes("/api/auth/login") ||
                           error.config?.url?.includes("/api/admin/auth/login");

    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Blog API
export const blogAPI = {
  getAll: (params) => api.get("/api/blogs", { params }),
  getById: (id) => api.get(`/api/blogs/${id}`),
  create: (data) => api.post("/api/blogs", data),
  update: (id, data) => api.put(`/api/blogs/${id}`, data),
  delete: (id) => api.delete(`/api/blogs/${id}`),
};

// News API
export const newsAPI = {
  getAll: (params) => api.get("/api/news", { params }),
  getById: (id) => api.get(`/api/news/${id}`),
  create: (data) => api.post("/api/news", data),
  update: (id, data) => api.put(`/api/news/${id}`, data),
  delete: (id) => api.delete(`/api/news/${id}`),
};

// Gallery API
export const galleryAPI = {
  getAll: (params) => api.get("/api/gallery", { params }),
  getByCategory: (category) => api.get("/api/gallery", { params: { category } }),
  submitImage: (data) => api.post("/api/gallery/submit", data),
  upload: (formData) =>
    api.post("/api/gallery", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/api/gallery/${id}`),
};

// Donation API
export const donationAPI = {
  // Public: create donation (legacy)
  create: (data) => api.post("/api/donations", data),
  // Admin: get all donations
  getAll: () => api.get("/api/donations"),
  // Private: get logged-in user's own donations + stats
  getUserDonations: (page = 1) => api.get(`/api/donations/my/list?page=${page}&limit=10`),
  // Private: initiate a donation record BEFORE opening Razorpay
  initiate: (data) => api.post("/api/donations/initiate", data),
  // Private: confirm payment after returning from Razorpay
  confirm: (id, razorpayData) => api.put(`/api/donations/${id}/confirm`, razorpayData),
  // Private: get single donation (for receipt)
  getById: (id) => api.get(`/api/donations/${id}`),
};

// Contact API
export const contactAPI = {
  create: (data) => api.post("/api/contact", data),
  getAll: () => api.get("/api/contact"),
};

// Google Translate API
export const translateText = async (text, targetLang = "en") => {
  try {
    const response = await axios.post(
      `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY}`,
      {
        q: text,
        target: targetLang,
        source: "hi",
      }
    );
    return response.data.data.translations[0].translatedText;
  } catch (error) {
    console.error("Translation error:", error);
    return text;
  }
};

export default api;
